import type { CommittedTransactionResponse } from "@aptos-labs/ts-sdk";
import type { WithdrawQueueEntry } from "@decibeltrade/sdk";

import { readDexSdk, sdkConfig } from "@/contexts/dex-sdk";
import { Logger } from "@/utils/logger";

const REQUEST_ID_REGEX = /^\d{1,20}$/;

/** Maps a cancel reason to its i18n key under `transferProgress.subtitles`. */
export function getCancelReasonI18nKey(
  reason: string | null | undefined,
):
  | "subtitles.cancelledInsufficientBalance"
  | "subtitles.cancelledDepositCheckFailed"
  | "subtitles.cancelledByUser"
  | "subtitles.cancelledGeneric" {
  switch (reason) {
    case "InsufficientWithdrawableBalance":
      return "subtitles.cancelledInsufficientBalance";
    case "DepositCheckFailed":
      return "subtitles.cancelledDepositCheckFailed";
    case "CancelledByUser":
      return "subtitles.cancelledByUser";
    default:
      return "subtitles.cancelledGeneric";
  }
}

// "unknown" covers edge cases where the tx response lacks an events field
// (e.g., API version changes or non-UserTransaction types). We treat it as
// ambiguous rather than assuming "processed", so we don't prematurely reset
// balance queries or skip polling.
export type WithdrawResult =
  | { type: "processed"; requestId: null }
  | { type: "queued"; requestId: string }
  | { type: "cancelled"; requestId: string }
  | { type: "unknown"; requestId: null };

const WITHDRAW_PROCESSED_EVENT = `${sdkConfig.deployment.package}::async_withdraw_queue::WithdrawProcessedEvent`;
const WITHDRAW_QUEUED_EVENT = `${sdkConfig.deployment.package}::async_withdraw_queue::WithdrawQueuedEvent`;
const WITHDRAW_CANCELLED_EVENT = `${sdkConfig.deployment.package}::async_withdraw_queue::WithdrawCancelledEvent`;

/**
 * Parses transaction events to determine if a withdrawal was processed immediately
 * or queued for later processing by the keeper.
 *
 * The on-chain contract emits:
 * - `WithdrawProcessedEvent` when the withdrawal completes within the same tx
 * - `WithdrawQueuedEvent` when the withdrawal is rate-limited and queued
 *
 * Both events may appear in the same tx (queued then immediately processed).
 * Processed takes precedence — the withdrawal completed in-tx.
 */
export function parseWithdrawEvents(txResponse: CommittedTransactionResponse): WithdrawResult {
  if (!("events" in txResponse)) {
    return { type: "unknown", requestId: null };
  }

  let hasProcessed = false;
  let hasCancelled = false;
  let queuedRequestId: string | null = null;
  let cancelledRequestId: string | null = null;

  for (const event of txResponse.events) {
    if (event.type === WITHDRAW_PROCESSED_EVENT) {
      hasProcessed = true;
    }
    if (event.type === WITHDRAW_QUEUED_EVENT) {
      const data = event.data as Record<string, unknown>;
      const requestId = parseRequestId(data.request_id);
      if (requestId) queuedRequestId = requestId;
    }
    if (event.type === WITHDRAW_CANCELLED_EVENT) {
      hasCancelled = true;
      const data = event.data as Record<string, unknown>;
      const requestId = parseRequestId(data.request_id);
      if (requestId) cancelledRequestId = requestId;
    }
  }

  // Priority: Processed > Cancelled > Queued.
  // Both Processed and Queued in same tx = queued then immediately processed.
  // Cancelled in same tx as Queued = queued then immediately cancelled
  // (e.g., InsufficientWithdrawableBalance detected during in-tx processing).
  if (hasProcessed) {
    if (hasCancelled) {
      Logger.warn("[withdraw-queue] Both Processed and Cancelled events in same tx", {
        queuedRequestId,
        cancelledRequestId,
      });
    }
    return { type: "processed", requestId: null };
  }
  if (hasCancelled && cancelledRequestId) {
    return { type: "cancelled", requestId: cancelledRequestId };
  }
  if (queuedRequestId) {
    return { type: "queued", requestId: queuedRequestId };
  }

  // No recognized event found — treat as ambiguous rather than assuming processed,
  // so callers don't prematurely reset balance queries or skip polling.
  return { type: "unknown", requestId: null };
}

function parseRequestId(value: unknown): string | null {
  return typeof value === "string" && REQUEST_ID_REGEX.test(value) ? value : null;
}

/** Determine the target step when a queued transfer is fully processed. */
export function getProcessedTargetStep(transfer: {
  type: "queued-withdraw" | "queued-xchain-withdraw";
  destinationChain: string;
}): "available" | "confirming" {
  if (transfer.type === "queued-withdraw" || transfer.destinationChain === "Aptos") {
    return "available";
  }
  return "confirming";
}

// ─── Shared pagination skeleton ──────────────────────────────────────────────

const PAGE_SIZE = 200;
const MAX_PAGES = 10;

/**
 * Paginate through getByAddr results, calling `onPage` for each page.
 * Return "done" from the callback to stop early with a result, or "continue" to keep paging.
 */
async function paginateWithdrawQueue<T>(
  userAddr: string,
  status: "Processed" | "Cancelled",
  signal: AbortSignal,
  onPage: (
    items: WithdrawQueueEntry[],
    pageNum: number,
  ) => { done: true; value: T } | { done: false },
): Promise<T | null> {
  let offset = 0;
  for (let pageNum = 0; pageNum < MAX_PAGES; pageNum++) {
    const page = await readDexSdk.withdrawQueue.getByAddr({
      subAddr: userAddr,
      status,
      limit: PAGE_SIZE,
      offset,
      fetchOptions: { signal },
    });
    const result = onPage(page.items, pageNum);
    if (result.done) return result.value;
    if (page.items.length < PAGE_SIZE) return null;
    offset += PAGE_SIZE;
  }
  return null;
}

/** Paginate through getByAddr results to find a specific requestId. */
export async function findEntryByRequestId(
  userAddr: string,
  status: "Processed" | "Cancelled",
  requestId: string,
  signal: AbortSignal,
) {
  return paginateWithdrawQueue(userAddr, status, signal, (items, pageNum) => {
    const match = items.find((e) => e.request_id === requestId);
    if (match) {
      if (pageNum > 0) {
        Logger.log("[withdraw-queue] findEntryByRequestId resolved on late page", {
          userAddr,
          status,
          requestId,
          pageNum,
        });
      }
      return { done: true, value: match };
    }
    return { done: false };
  });
}

/**
 * Sum `processed_amount` across ALL Processed rows for a given `request_id`.
 * Partial fills emit separate rows. Pass `requestedAmount` to enable early exit.
 */
export async function sumProcessedByRequestId(
  userAddr: string,
  requestId: string,
  signal: AbortSignal,
  requestedAmount?: number,
): Promise<{ total: number; found: boolean }> {
  let total = 0;
  let found = false;

  await paginateWithdrawQueue(userAddr, "Processed", signal, (items) => {
    for (const entry of items) {
      if (entry.request_id === requestId) {
        total += entry.processed_amount;
        found = true;
      }
    }
    if (requestedAmount != null && total >= requestedAmount - 1e-6) {
      return { done: true, value: null };
    }
    return { done: false };
  });

  return { total, found };
}
